import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ShipmentRouteStepType, ShipmentStopType } from '@nest/domain-shipments-shared';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
export const GOOGLE_MAPS_API_KEY = 'AIzaSyBZxPkkcMyP853cqURGKSGEo354J76lCDc';
function mapGpsToLatLng(gps) {
    return { lat: gps.lat, lng: gps.lng };
}
function computeBounds(points) {
    const bounds = new google.maps.LatLngBounds();
    for (const p of points) {
        bounds.extend(p);
    }
    return bounds;
}
export const ShipmentGoogleMap = memo(({ routes, unassignedRouteSteps, defaultCenter, indexHighlightedRoute = null, indexHighlightedStopOfSelectedRoute = null, vehicles = [], loading = false, height = 520, routeLineWidth = 8, highlightedRouteLineWidth = 12, stopMarkerSize = 10, highlightedStopMarkerSize = 14, onSelectRouteId, onSelectStopId, onUnasignedPointClick }) => {
    const { isLoaded } = useJsApiLoader({ id: 'google-maps-script', googleMapsApiKey: GOOGLE_MAPS_API_KEY, libraries: ['places'] });
    const mapRef = useRef(null);
    const polylinesRef = useRef(new Map());
    const containerStyle = useMemo(() => ({ width: '100%', height }), [height]);
    const handleMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);
    const allPoints = useMemo(() => {
        const pts = [];
        for (const route of routes) {
            for (const stop of route.stops) {
                pts.push(mapGpsToLatLng(stop.location));
            }
        }
        return pts;
    }, [routes]);
    const unassignedGroups = useMemo(() => {
        if (!unassignedRouteSteps) {
            return [];
        }
        const groups = [];
        for (const steps of Object.values(unassignedRouteSteps)) {
            if ((steps?.length ?? 0) > 0) {
                const first = steps[0];
                groups.push({ position: mapGpsToLatLng(first.location), steps });
            }
        }
        return groups;
    }, [unassignedRouteSteps]);
    const unassignedPoints = useMemo(() => {
        return unassignedGroups.map(g => g.position);
    }, [unassignedGroups]);
    const allPointsIncludingUnassigned = useMemo(() => {
        return [...allPoints, ...unassignedPoints];
    }, [allPoints, unassignedPoints]);
    const vehicleColorById = useMemo(() => {
        const map = new Map();
        for (const route of routes) {
            if (route.vehicle?.id != null && route.vehicle?.color) {
                map.set(route.vehicle.id, route.vehicle.color);
            }
        }
        return map;
    }, [routes]);
    useEffect(() => {
        if (!isLoaded || !mapRef.current) {
            return;
        }
        if (indexHighlightedRoute !== null && indexHighlightedRoute >= 0 && routes[indexHighlightedRoute]) {
            const route = routes[indexHighlightedRoute];
            const routePoints = route.stops.map(s => mapGpsToLatLng(s.location));
            const bounds = computeBounds(routePoints);
            mapRef.current.fitBounds(bounds, 64);
            return;
        }
        if (allPointsIncludingUnassigned.length > 0) {
            const bounds = computeBounds(allPointsIncludingUnassigned);
            mapRef.current.fitBounds(bounds, 64);
            return;
        }
    }, [allPointsIncludingUnassigned, indexHighlightedRoute, isLoaded, routes]);
    useEffect(() => {
        if (!isLoaded || !mapRef.current) {
            return;
        }
        if (indexHighlightedRoute !== null &&
            indexHighlightedRoute >= 0 &&
            routes[indexHighlightedRoute] &&
            indexHighlightedStopOfSelectedRoute !== null &&
            indexHighlightedStopOfSelectedRoute >= 0) {
            const route = routes[indexHighlightedRoute];
            const stops = route.stops;
            const stopIndex = Math.min(indexHighlightedStopOfSelectedRoute, stops.length - 1);
            const neighborIndices = [Math.max(0, stopIndex - 1), stopIndex, Math.min(stops.length - 1, stopIndex + 1)];
            const points = neighborIndices.map(i => mapGpsToLatLng(stops[i].location));
            const bounds = computeBounds(points);
            mapRef.current.fitBounds(bounds, 96);
        }
    }, [indexHighlightedRoute, indexHighlightedStopOfSelectedRoute, isLoaded, routes]);
    // Imperative management of polylines (prevents stale overlays)
    useEffect(() => {
        if (!isLoaded || !mapRef.current) {
            return;
        }
        const map = mapRef.current;
        const shouldExist = new Set();
        routes.forEach((route, routeIndex) => {
            const key = String(route.id ?? routeIndex);
            shouldExist.add(key);
            const path = route.stops.map(s => mapGpsToLatLng(s.location));
            const color = route.vehicle?.color ?? '#3b82f6';
            const isHighlighted = indexHighlightedRoute === routeIndex;
            const weight = isHighlighted ? highlightedRouteLineWidth : routeLineWidth;
            const existing = polylinesRef.current.get(key);
            if (path.length < 2) {
                if (existing) {
                    existing.setMap(null);
                    polylinesRef.current.delete(key);
                }
                return;
            }
            if (existing) {
                existing.setOptions({ strokeColor: color, strokeOpacity: 1, strokeWeight: weight });
                existing.setPath(path);
                existing.setMap(map);
                // Refresh click listener to reflect current route id/index
                google.maps.event.clearInstanceListeners(existing);
                existing.addListener('click', () => onSelectRouteId?.(route.id ?? routeIndex));
            }
            else {
                const poly = new google.maps.Polyline({ map, path, strokeColor: color, strokeOpacity: 1, strokeWeight: weight, clickable: true });
                if (onSelectRouteId) {
                    poly.addListener('click', () => onSelectRouteId(route.id ?? routeIndex));
                }
                polylinesRef.current.set(key, poly);
            }
        });
        for (const [key, poly] of polylinesRef.current.entries()) {
            if (!shouldExist.has(key)) {
                poly.setMap(null);
                polylinesRef.current.delete(key);
            }
        }
    }, [isLoaded, routes, indexHighlightedRoute, routeLineWidth, highlightedRouteLineWidth, onSelectRouteId]);
    // Clear on unmount
    useEffect(() => {
        return () => {
            for (const [, poly] of polylinesRef.current.entries()) {
                poly.setMap(null);
            }
            polylinesRef.current.clear();
        };
    }, []);
    if (!isLoaded) {
        return _jsx("div", { className: "w-full", style: { height } });
    }
    return (_jsxs("div", { className: "relative w-full", children: [_jsxs(GoogleMap, { onLoad: handleMapLoad, mapContainerStyle: containerStyle, center: defaultCenter, zoom: 10, options: { clickableIcons: false }, children: [routes.map((route, routeIndex) => {
                        const color = route.vehicle?.color ?? '#3b82f6';
                        const isHighlighted = indexHighlightedRoute === routeIndex;
                        const baseKey = String(route.vehicle?.id ?? routeIndex);
                        return (_jsx(React.Fragment, { children: route.stops.map((stop, idx) => {
                                const position = mapGpsToLatLng(stop.location);
                                const isStopHighlighted = isHighlighted && indexHighlightedStopOfSelectedRoute === idx;
                                const markerLabel = stop.type === ShipmentStopType.Start ? 'S' : stop.type === ShipmentStopType.End ? 'C' : String(idx);
                                return (_jsx(Marker, { position: position, onClick: () => {
                                        onSelectStopId?.({
                                            routeId: route.id ?? routeIndex,
                                            stopId: stop.id ?? idx
                                        });
                                    }, icon: {
                                        path: google.maps.SymbolPath.CIRCLE,
                                        scale: isStopHighlighted ? highlightedStopMarkerSize : stopMarkerSize,
                                        fillColor: color,
                                        fillOpacity: 1,
                                        strokeWeight: 2,
                                        strokeColor: color
                                    }, label: {
                                        text: markerLabel,
                                        color: '#ffffff',
                                        fontSize: `${Math.round((isStopHighlighted ? highlightedStopMarkerSize : stopMarkerSize) + 2)}`,
                                        fontWeight: isStopHighlighted ? 'bold' : 'normal'
                                    } }, `${baseKey}-stop-${idx}-${stop.id ?? 'x'}`));
                            }) }, `route-${baseKey}`));
                    }), vehicles.map(v => {
                        const idAsNumber = typeof v.id === 'string' ? Number(v.id) : v.id;
                        const colorFromRoutes = v.vehicleId != null ? vehicleColorById.get(v.vehicleId) : Number.isFinite(idAsNumber) ? vehicleColorById.get(idAsNumber) : undefined;
                        const color = colorFromRoutes ?? v.color ?? '#111827';
                        return (_jsx(Marker, { position: { lat: v.lat, lng: v.lng }, icon: {
                                path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                                scale: 5,
                                fillColor: color,
                                fillOpacity: 1,
                                strokeWeight: 1,
                                strokeColor: color
                            }, label: v.label ? { text: v.label, color: '#ffffff', fontSize: '10px' } : undefined }, `vehicle-${v.id}`));
                    }), unassignedGroups.map((group, idx) => {
                        const position = group.position;
                        const unassignedMarkerSize = stopMarkerSize;
                        const unassignedColor = '#ef4444';
                        return (_jsx(Marker, { position: position, onClick: () => {
                                const documents = [];
                                const stepType = group.steps[0]?.stepType ?? ShipmentRouteStepType.Job;
                                for (const step of group.steps) {
                                    if (step.stepType === ShipmentRouteStepType.Pickup) {
                                        if (step.loadDocument) {
                                            documents.push(step.loadDocument);
                                        }
                                    }
                                    else if (step.stepType === ShipmentRouteStepType.Delivery || step.stepType === ShipmentRouteStepType.Job) {
                                        if (step.unloadDocument) {
                                            documents.push(step.unloadDocument);
                                        }
                                    }
                                }
                                onUnasignedPointClick?.({ documents, stepType });
                            }, icon: {
                                path: google.maps.SymbolPath.CIRCLE,
                                scale: unassignedMarkerSize,
                                fillColor: unassignedColor,
                                fillOpacity: 1,
                                strokeWeight: 0
                            } }, `unassigned-${idx}`));
                    })] }), loading ? (_jsx("div", { className: "pointer-events-none absolute inset-0 flex items-center justify-center bg-white/50", children: _jsx("div", { className: "h-8 w-8 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" }) })) : null] }));
});
ShipmentGoogleMap.displayName = 'ShipmentGoogleMap';
//# sourceMappingURL=ShipmentGoogleMap.js.map